import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { buildReturnParam } from '../utils/returnNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, Search, TrendingUp, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { formatInCST, formatDateOnly, daysOverdue } from '../utils/timezone';
import { AR_COLUMNS, sortInvoices, toCSV } from '../utils/ar-report';

const AR_FETCH_CAP = 10000; // hard ceiling; backend max page is 10000

export default function AccountsReceivable() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [sort, setSort] = useState({
    key: searchParams.get('sort') || 'issue_date',
    direction: searchParams.get('dir') === 'asc' ? 'asc' : 'desc',
  });
  const [exporting, setExporting] = useState(false);
  const [truncated, setTruncated] = useState(false);

  // Keep the URL in sync with filters/sort so a refresh restores this exact view
  // and an origin-aware back link can return here with the same query.
  useEffect(() => {
    const next = {};
    if (statusFilter && statusFilter !== 'all') next.status = statusFilter;
    if (searchTerm) next.q = searchTerm;
    if (sort.key !== 'issue_date') next.sort = sort.key;
    if (sort.direction !== 'desc') next.dir = sort.direction;
    setSearchParams(next, { replace: true });
  }, [statusFilter, searchTerm, sort, setSearchParams]);

  const openInvoice = (id) => navigate(`/invoices/${id}?from=${buildReturnParam(location)}`);

  const loadData = async () => {
    setLoading(true);
    try {
      const statusParam = statusFilter !== 'all' ? { status: statusFilter } : {};
      const pageSize = 1000;
      const fetchAll = async () => {
        let skip = 0;
        let all = [];
        let total = null;
        while (skip < AR_FETCH_CAP) {
          const res = await api.get('/invoices', {
            params: { ...statusParam, skip, limit: pageSize, sort_by: 'issue_date', sort_order: 'desc' },
          });
          const data = res.data;
          const batch = Array.isArray(data) ? data : (data.invoices || []);
          total = Array.isArray(data) ? batch.length : (data.total ?? batch.length);
          all = all.concat(batch);
          if (batch.length < pageSize || all.length >= total) break;
          skip += pageSize;
        }
        return { all, total: total ?? all.length };
      };
      const [{ all, total }, summaryRes] = await Promise.all([
        fetchAll(),
        api.get('/accounting/ar-summary'),
      ]);
      setInvoices(all);
      setTruncated(total > all.length);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load AR data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success('Invoice status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const isOverdue = (invoice) => {
    return invoice.status !== 'paid' && invoice.status !== 'cancelled' &&
           !!invoice.due_date && daysOverdue(invoice.due_date) > 0;
  };

  const getDaysOverdue = (dueDate) => daysOverdue(dueDate);

  const matchingInvoices = invoices.filter(invoice => {
    const matchesSearch = !searchTerm || 
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredInvoices = sortInvoices(matchingInvoices, sort.key, sort.direction);
  const toggleSort = key => setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }));
  const reportRows = () => filteredInvoices.map(invoice => [
    invoice.invoice_number || '-', invoice.customer_name || '-',
    invoice.issue_date ? formatDateOnly(invoice.issue_date, 'MMM d, yyyy') : '-',
    invoice.due_date ? formatDateOnly(invoice.due_date, 'MMM d, yyyy') : '-',
    '$' + Number(invoice.total || 0).toFixed(2), invoice.status || '-',
    isOverdue(invoice) ? getDaysOverdue(invoice.due_date) + ' days' : invoice.status === 'paid' ? 'Paid' : '-'
  ]);
  const downloadReport = (blob, extension) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url;
    link.download = 'CipherOne-Accounts-Receivable-' + formatInCST(new Date(), 'yyyy-MM-dd') + '.' + extension;
    document.body.appendChild(link); link.click(); link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  const exportCSV = () => downloadReport(new Blob(['﻿', toCSV([AR_COLUMNS.map(x => x[1]), ...reportRows()])], { type: 'text/csv;charset=utf-8' }), 'csv');
  const exportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.post('/export/accounts-receivable/pdf', { rows: reportRows(), filter_label: 'Status: ' + statusFilter + (searchTerm ? '; Search: ' + searchTerm : '') + '; Sort: ' + AR_COLUMNS.find(x=>x[0]===sort.key)[1] + ' ' + sort.direction }, { responseType: 'blob' });
      downloadReport(response.data, 'pdf');
    } catch (error) { toast.error('PDF export failed. Try again or narrow the filter (maximum 5,000 invoices).'); }
    finally { setExporting(false); }
  };

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading AR data...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-4 sm:space-y-6" data-testid="ar-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Accounts Receivable</h1>
        <p className="text-gray-600 text-sm sm:text-base mt-1">Track customer payments and outstanding invoices</p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Receivable</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">${summary.total_receivable.toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices?status=overdue')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                Overdue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{summary.overdue_invoices}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices?status=sent')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">0-30 Days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.aging_0_30}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices?status=sent')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">31-60 Days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.aging_31_60}</div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/invoices?status=overdue')}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">61+ Days</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.aging_61_plus}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {summary && (
        <Card className="co-revenue-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Revenue This Month</p>
                <p className="text-4xl font-bold text-green-600">${summary.revenue_this_month.toFixed(2)}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-green-600" />
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[180px]">
              <Search className="h-5 w-5 text-gray-400" />
              <Input placeholder="Search invoices..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-sm" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportCSV} disabled={!filteredInvoices.length}>Export CSV</Button>
            <Button variant="outline" onClick={exportPDF} disabled={!filteredInvoices.length || exporting}>{exporting ? 'Exporting...' : 'Export PDF'}</Button>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-3 text-sm text-muted-foreground">
            <span aria-live="polite">{filteredInvoices.length} invoices · exports match this view</span>
            {truncated && (
              <span className="text-amber-600" data-testid="ar-truncated-notice">
                Showing the first {invoices.length.toLocaleString()} invoices; refine the status filter to see the rest.
              </span>
            )}
            <label className="flex items-center gap-2">Sort by
              <select aria-label="Sort invoices by" value={sort.key} onChange={e=>setSort(current=>({...current,key:e.target.value}))}>
                {AR_COLUMNS.map(([key,label])=><option key={key} value={key}>{label}</option>)}
              </select>
            </label>
            <Button variant="outline" size="sm" onClick={()=>setSort(current=>({...current,direction:current.direction==='asc'?'desc':'asc'}))}>{sort.direction==='asc'?'Ascending ↑':'Descending ↓'}</Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-2" data-testid="ar-mobile-cards">
            {filteredInvoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No invoices found
              </div>
            ) : filteredInvoices.map((invoice) => {
              const overdue = isOverdue(invoice);
              const daysOverdue = overdue ? getDaysOverdue(invoice.due_date) : 0;
              return (
                <div
                  key={invoice.id}
                  className={`border rounded-lg p-3 cursor-pointer hover:shadow-sm transition-shadow ${overdue ? 'bg-red-50 border-red-200' : 'bg-[#081a25]'}`}
                  onClick={() => openInvoice(invoice.id)}
                  data-testid={`ar-card-${invoice.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold">{invoice.invoice_number}</span>
                        <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                        {overdue && <span className="text-xs text-red-600 font-semibold">{daysOverdue}d overdue</span>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{invoice.customer_name}</p>
                    </div>
                    <span className="font-semibold whitespace-nowrap">${invoice.total.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                    <span>Due {invoice.due_date ? formatDateOnly(invoice.due_date, 'MMM d, yyyy') : '-'}</span>
                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                      <Select value={invoice.status} onValueChange={(value) => handleStatusChange(invoice.id, value)}>
                        <SelectTrigger className="w-[80px] h-7 text-xs" onClick={(e) => e.stopPropagation()}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="cancelled">Cancel</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead aria-sort={sort.key === "invoice_number" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("invoice_number")}>Invoice # <span aria-hidden="true">{sort.key === "invoice_number" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead aria-sort={sort.key === "customer_name" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("customer_name")}>Customer <span aria-hidden="true">{sort.key === "customer_name" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead aria-sort={sort.key === "issue_date" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("issue_date")}>Issue Date <span aria-hidden="true">{sort.key === "issue_date" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead aria-sort={sort.key === "due_date" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("due_date")}>Due Date <span aria-hidden="true">{sort.key === "due_date" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead aria-sort={sort.key === "total" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("total")}>Amount <span aria-hidden="true">{sort.key === "total" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead aria-sort={sort.key === "status" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("status")}>Status <span aria-hidden="true">{sort.key === "status" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead aria-sort={sort.key === "aging" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}><button type="button" className="inline-flex items-center gap-1 rounded px-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => toggleSort("aging")}>Aging <span aria-hidden="true">{sort.key === "aging" ? (sort.direction === "asc" ? "↑" : "↓") : "↕"}</span></button></TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    No invoices found
                  </TableCell>
                </TableRow>
              ) : (
                filteredInvoices.map((invoice) => {
                  const overdue = isOverdue(invoice);
                  const daysOverdue = overdue ? getDaysOverdue(invoice.due_date) : 0;
                  
                  return (
                    <TableRow key={invoice.id} className={`cursor-pointer hover:bg-gray-50 ${overdue ? 'bg-red-50 hover:bg-red-100' : ''}`} onClick={() => openInvoice(invoice.id)}>
                      <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.customer_name}</TableCell>
                      <TableCell>{formatDateOnly(invoice.issue_date, 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        {invoice.due_date ? formatDateOnly(invoice.due_date, 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="font-semibold">${invoice.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {overdue ? (
                          <span className="text-red-600 font-semibold">{daysOverdue} days</span>
                        ) : invoice.status === 'paid' ? (
                          <span className="text-green-600">Paid</span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                          <Select value={invoice.status} onValueChange={(value) => handleStatusChange(invoice.id, value)}>
                            <SelectTrigger className="w-[120px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="sent">Sent</SelectItem>
                              <SelectItem value="paid">Paid</SelectItem>
                              <SelectItem value="cancelled">Cancel</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
